import { createRouter, createWebHistory } from 'vue-router';
import { Capacitor } from '@capacitor/core';
import { useAuthStore } from '../stores/auth';
import { userManager } from '../services/oidc';

// View components
const Home = () => import('../views/Home.vue');
const Login = () => import('../views/Login.vue');
const Register = () => import('../views/Register.vue');
const Callback = () => import('../views/Callback.vue');
const Profile = () => import('../views/Profile.vue');

// Circle Views
const Circles = () => import('../views/circles/Circles.vue');
const CircleDetail = () => import('../views/circles/CircleDetail.vue');
const CircleForm = () => import('../views/circles/CircleForm.vue');

// Restaurant Views
const Restaurants = () => import('../views/restaurants/Restaurants.vue');
const RestaurantDetail = () => import('../views/restaurants/RestaurantDetail.vue');
const RestaurantForm = () => import('../views/restaurants/RestaurantForm.vue');

// Visit Views
const Visits = () => import('../views/visits/Visits.vue');
const VisitDetail = () => import('../views/visits/VisitDetail.vue');
const VisitForm = () => import('../views/visits/VisitForm.vue');

// Review Views
const ReviewForm = () => import('../views/reviews/ReviewForm.vue');
const ReviewDetail = () => import('../views/reviews/ReviewDetail.vue');
const ReviewResults = () => import('../views/reviews/ReviewResults.vue');

// Public (login-free) Views
const PublicCircle = () => import('../views/PublicCircle.vue');

// Error Views
const NotFound = () => import('../views/NotFound.vue');

const routes = [
	{
		path: '/',
		name: 'Home',
		component: Home,
		meta: { requiresAuth: true }
	},
	{
		path: '/login',
		name: 'Login',
		component: Login,
		meta: { public: true }
	},
	{
		path: '/register',
		name: 'Register',
		component: Register,
		meta: { public: true }
	},
	{
		// OIDC auth-code redirect target. Must be public so the guard doesn't
		// bounce it back to the IdP mid-callback.
		path: '/callback',
		name: 'Callback',
		component: Callback,
		meta: { public: true }
	},
	{
		path: '/profile',
		name: 'Profile',
		component: Profile,
		meta: { requiresAuth: true }
	},
	// Public, login-free circle page. `public: true` (not requiresAuth) so the
	// guard lets it through for logged-out visitors.
	{
		path: '/c/:slug',
		name: 'PublicCircle',
		component: PublicCircle,
		meta: { public: true }
	},
	// Circle routes
	{
		path: '/circles',
		name: 'Circles',
		component: Circles,
		meta: { requiresAuth: true }
	},
	{
		path: '/circles/new',
		name: 'NewCircle',
		component: CircleForm,
		meta: { requiresAuth: true }
	},
	{
		path: '/circles/:id',
		name: 'CircleDetail',
		component: CircleDetail,
		meta: { requiresAuth: true }
	},
	{
		path: '/circles/:id/edit',
		name: 'EditCircle',
		component: CircleForm,
		meta: { requiresAuth: true }
	},
	// Restaurant routes
	{
		path: '/restaurants',
		name: 'Restaurants',
		component: Restaurants,
		meta: { requiresAuth: true }
	},
	{
		path: '/restaurants/new',
		name: 'NewRestaurant',
		component: RestaurantForm,
		meta: { requiresAuth: true }
	},
	{
		path: '/restaurants/:id',
		name: 'RestaurantDetail',
		component: RestaurantDetail,
		meta: { requiresAuth: true }
	},
	{
		path: '/restaurants/:id/edit',
		name: 'EditRestaurant',
		component: RestaurantForm,
		meta: { requiresAuth: true }
	},
	// Visit routes
	{
		path: '/visits',
		name: 'Visits',
		component: Visits,
		meta: { requiresAuth: true }
	},
	{
		path: '/visits/new',
		name: 'NewVisit',
		component: VisitForm,
		meta: { requiresAuth: true }
	},
	{
		path: '/visits/:id',
		name: 'VisitDetail',
		component: VisitDetail,
		meta: { requiresAuth: true }
	},
	{
		path: '/visits/:id/edit',
		name: 'EditVisit',
		component: VisitForm,
		meta: { requiresAuth: true }
	},
	// Review routes
	{
		path: '/visits/:visitId/reviews/new',
		name: 'NewReview',
		component: ReviewForm,
		meta: { requiresAuth: true }
	},
	{
		path: '/reviews/:id',
		name: 'ReviewDetail',
		component: ReviewDetail,
		meta: { requiresAuth: true }
	},
	{
		path: '/reviews/:id/edit',
		name: 'EditReview',
		component: ReviewForm,
		meta: { requiresAuth: true }
	},
	{
		path: '/visits/:visitId/reviews',
		name: 'ReviewResults',
		component: ReviewResults,
		meta: { requiresAuth: true }
	},
	// Catch-all 404 route - must be last
	{
		path: '/:pathMatch(.*)*',
		name: 'NotFound',
		component: NotFound
	}
];

const router = createRouter({
	history: createWebHistory(process.env.BASE_URL),
	routes
});

// Navigation guard. Unauthenticated access to a protected route kicks off an
// OIDC sign-in redirect (auth-code + PKCE) straight to the IdP. Public routes
// (/login, /register, /callback) pass through untouched.
router.beforeEach(async (to) => {
	const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
	if (!requiresAuth) return true;

	const authStore = useAuthStore();
	const isAuthenticated = await authStore.checkAuth();
	if (!isAuthenticated) {
		// Native can't leave the WebView for the IdP — send the user to the Login screen, which
		// drives the external-browser sign-in on tap. Web redirects straight to the IdP.
		if (Capacitor.isNativePlatform()) {
			return { name: 'Login', query: { redirect: to.fullPath } };
		}
		userManager.signinRedirect({ state: { redirect: to.fullPath } });
		return false; // halt navigation; the browser is leaving for the IdP
	}
	return true;
});

export default router;
